import type { PayloadAction } from '@reduxjs/toolkit';
import { createSlice, current } from '@reduxjs/toolkit';
import type { Car, CarRequest, FilesDto, InitialState } from './types';
import { NEW_CAR_INITIAL_VALUE } from './constants';

const initialState: InitialState = {
  items: [],
  isPending: false,
  searchResult: [],
  newCar: NEW_CAR_INITIAL_VALUE,
  selectedCar: null,
  carChangedKeys: [],
  totalCount: 0,
  carCreationProgress: null,
  mediaUploadProgress: 0,
  filesBeingDeleted: [],
  carDocumentPendingStatus: {
    registrationDoc: false,
    insuranceDoc: false,
    insuranceInformation: false,
  },
};

export const carsSlice = createSlice({
  name: 'cars',
  initialState,
  reducers: {
    setFilesBeingDeleted: (state, action: PayloadAction<string>) => {
      return {
        ...state,
        filesBeingDeleted: state.filesBeingDeleted.includes(action.payload)
          ? state.filesBeingDeleted.filter((item) => item !== action.payload)
          : state.filesBeingDeleted.concat(action.payload),
      };
    },
    setMediaUploadProgress: (state, action: PayloadAction<number>) => {
      return {
        ...state,
        mediaUploadProgress: action.payload,
      };
    },
    resetStore: (state) => {
      return {
        ...state,
        ...initialState,
        items: state.items,
      };
    },
    removeInitialCarImage: (state, action: PayloadAction<string>) => {
      return {
        ...state,
        items: state.items.map((item) =>
          item.id === state.selectedCar?.id
            ? {
                ...item,
                uploadedFiles: item.uploadedFiles.filter(
                  (item) => item.id !== action.payload,
                ),
              }
            : item,
        ),
        selectedCar: {
          ...(state.selectedCar as Car),
          uploadedFiles: (state.selectedCar?.uploadedFiles || []).filter(
            (item) => item.id !== action.payload,
          ),
        },
      };
    },
    setCarDocumentPendingStatus: (
      state,
      action: PayloadAction<{
        key: 'registrationDoc' | 'insuranceDoc' | 'insuranceInformation';
        status: boolean;
      }>,
    ) => {
      return {
        ...state,
        carDocumentPendingStatus: {
          ...state.carDocumentPendingStatus,
          [action.payload.key]: action.payload.status,
        },
      };
    },
    setCarCreationProgress: (state, action: PayloadAction<number | null>) => {
      return {
        ...state,
        carCreationProgress: action.payload,
      };
    },
    setSearchItems: (state, action: PayloadAction<Array<Car>>) => {
      return {
        ...state,
        searchResult: action.payload,
      };
    },
    setTotalCount: (state, action: PayloadAction<number>) => {
      return {
        ...state,
        totalCount: action.payload,
      };
    },
    updateCars: (state, action: PayloadAction<Array<Car>>) => {
      return {
        ...state,
        items: state.items.map((item) => {
          const updatedCar = action.payload.find((car) => car.id === item.id);
          return updatedCar ? updatedCar : item;
        }),
      };
    },
    setSelectedCarChangedKeys: (
      state,
      action: PayloadAction<keyof Car | []>,
    ) => {
      return {
        ...state,
        carChangedKeys: Array.from(
          new Set(state.carChangedKeys.concat(action.payload)),
        ),
      };
    },
    removeCars: (state, action: PayloadAction<Array<string>>) => {
      return {
        ...state,
        totalCount: state.totalCount - action.payload.length,
        items: state.items.filter(
          (car) => !action.payload.includes(car.id as string),
        ),
      };
    },
    updateCar: (state, action: PayloadAction<Car>) => {
      return {
        ...state,
        items: state.items.map((item) =>
          item.id === action.payload.id ? action.payload : item,
        ),
      };
    },
    addNewCar: (state, action: PayloadAction<Car>) => {
      return {
        ...state,
        items: state.items.concat(action.payload),
      };
    },
    resetNewCarState: (state) => {
      return {
        ...state,
        newCar: NEW_CAR_INITIAL_VALUE,
      };
    },
    setCarsPendingStatus: (state, action: PayloadAction<boolean>) => {
      return {
        ...state,
        isPending: action.payload,
      };
    },
    setNewCar: (state, action: PayloadAction<Partial<CarRequest>>) => {
      return {
        ...state,
        newCar: {
          ...state.newCar,
          ...action.payload,
        },
      };
    },
    setCars: (state, action: PayloadAction<Array<Car>>) => {
      return {
        ...state,
        items: action.payload,
      };
    },
    updateCarUploadedFiles: (state, action: PayloadAction<Array<FilesDto>>) => {
      const selectedCar = current(state.selectedCar) as Car;
      return {
        ...state,
        selectedCar: {
          ...selectedCar,
          uploadedFiles: action.payload,
        },
      };
    },
    setSelectedCar: (state, action: PayloadAction<Car | null>) => {
      return {
        ...state,
        selectedCar: action.payload,
      };
    },
  },
});
